// ================================================================
// src/utils/deviceInfo.js
//
// Captura timezone, dispositivo, navegador e idioma del browser
// y los envía al backend UNA VEZ por sesión.
//
// USO — pegar en App.jsx (o donde tengas el useEffect de auth):
//
//   import { syncDeviceInfo } from "./utils/deviceInfo";
//
//   useEffect(() => {
//     const token = localStorage.getItem("token");
//     if (token) syncDeviceInfo(token);
//   }, []);
//
// ================================================================

// ── Parser de User-Agent → { device, browser } ────────────────

function parseUserAgent(ua = navigator.userAgent) {
  // Dispositivo
  let device = "Desktop";
  if (/iPhone/i.test(ua))        device = `iPhone · iOS ${getIOSVersion(ua)}`;
  else if (/iPad/i.test(ua))     device = `iPad · iPadOS ${getIOSVersion(ua)}`;
  else if (/Android/i.test(ua)) {
    const model = ua.match(/;\s([^;)]+)\sBuild/)?.[1] || "Android";
    const ver   = ua.match(/Android\s([\d.]+)/)?.[1] || "";
    device = `${model} · Android ${ver}`;
  } else if (/Macintosh/i.test(ua)) {
    const ver = ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, ".") || "";
    device = `MacBook · macOS ${ver}`;
  } else if (/Windows/i.test(ua)) {
    const ver = ua.match(/Windows NT ([\d.]+)/)?.[1];
    const name = { "10.0": "10/11", "6.3": "8.1", "6.1": "7" }[ver] || ver || "";
    device = `Windows ${name}`;
  } else if (/Linux/i.test(ua)) {
    device = "Linux";
  }

  // Navegador
  let browser = "Unknown";
  if (/Edg\//i.test(ua)) {
    const v = ua.match(/Edg\/([\d.]+)/)?.[1]?.split(".")[0] || "";
    browser = `Edge ${v}`;
  } else if (/OPR\//i.test(ua) || /Opera/i.test(ua)) {
    const v = ua.match(/OPR\/([\d.]+)/)?.[1]?.split(".")[0] || "";
    browser = `Opera ${v}`;
  } else if (/Firefox\//i.test(ua)) {
    const v = ua.match(/Firefox\/([\d.]+)/)?.[1]?.split(".")[0] || "";
    browser = `Firefox ${v}`;
  } else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) {
    const v = ua.match(/Chrome\/([\d.]+)/)?.[1]?.split(".")[0] || "";
    browser = `Chrome ${v}`;
  } else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) {
    const v = ua.match(/Version\/([\d.]+)/)?.[1]?.split(".")[0] || "";
    browser = `Safari ${v}`;
  }

  return { device, browser };
}

function getIOSVersion(ua) {
  return ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, ".") || "";
}

// ── Main export ────────────────────────────────────────────────

export async function syncDeviceInfo(token) {
  // Solo enviar una vez por sesión (evita llamadas repetidas)
  const KEY = "device_synced_at";
  const last = sessionStorage.getItem(KEY);
  // 🛑 COMENTADA TEMPORALMENTE PARA FORZAR EL ENVÍO EN CADA CARGA:
  // if (last) return;   // ya se envió en esta sesión

  try {
    const { device, browser } = parseUserAgent();

    const payload = {
      timezone:       Intl.DateTimeFormat().resolvedOptions().timeZone,  // "America/Mexico_City"
      device_info:    device,    // "iPhone 14 · iOS 17"
      browser_info:   browser,   // "Safari 17"
      preferred_lang: navigator.language,  // "es-MX"
      // IP la lee el backend desde request.client.host / X-Forwarded-For
    };

    // Agregamos un log para confirmar visualmente antes del fetch
    console.log("🚀 DISPARANDO AL BACKEND:", payload);

    const res = await fetch("/api/profile/device-info", {
      method:  "POST",
      headers: {
        "Content-Type":  "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      sessionStorage.setItem(KEY, Date.now().toString());
      console.log("✅ Device info synced:", payload);
    }
  } catch (err) {
    // No crítico — falla silenciosa
    console.warn("Device info sync skipped:", err.message);
  }
}